type IIdToLoaderData = Record<string, any>;

import type { PictureSourceSpec } from './pictures';

// eslint-disable-next-line @opendoor/no-direct-env-check
const DEPLOY_ENV = process.env.NEXT_PUBLIC_DEPLOY_ENV || 'development';
const LOCAL_COSMOS_URL = process.env.LOCAL_COSMOS_URL;

const trimTrailingSlash = (value: string): string => value.replace(/\/$/, '');

export const getCosmosAssetOrigin = ({
  deployEnv = DEPLOY_ENV,
  localCosmosUrl = LOCAL_COSMOS_URL,
}: {
  deployEnv?: string;
  localCosmosUrl?: string;
} = {}): string => {
  if (deployEnv === 'production') return 'https://cdn-cosmos.opendoor.com';
  if (deployEnv === 'qa' || deployEnv === 'staging') return 'https://cdn-cosmos.simplersell.com';
  if (deployEnv === 'localdev' && localCosmosUrl) return trimTrailingSlash(localCosmosUrl);
  return '';
};

export const COSMOS_ASSET_ORIGIN = getCosmosAssetOrigin();

export type FontPreload = {
  href: string;
  as: 'font';
  type: 'font/woff2';
  crossOrigin: 'anonymous';
};

const makeFontPreload = (path: string): FontPreload => ({
  href: `${COSMOS_ASSET_ORIGIN}${path}`,
  as: 'font',
  type: 'font/woff2',
  crossOrigin: 'anonymous',
});

const GRAPHIK_REGULAR_FONT_PRELOAD = makeFontPreload(
  '/fonts/graphik-regular/Graphik-Regular.woff2',
);
const GRAPHIK_MEDIUM_FONT_PRELOAD = makeFontPreload('/fonts/graphik-medium/Graphik-Medium.woff2');
const GRAPHIK_SEMIBOLD_FONT_PRELOAD = makeFontPreload(
  '/fonts/graphik-semibold/Graphik-Semibold.woff2',
);

export const CONTENT_PAGE_FONT_PRELOADS: ReadonlyArray<FontPreload> = [
  GRAPHIK_REGULAR_FONT_PRELOAD,
  GRAPHIK_MEDIUM_FONT_PRELOAD,
  GRAPHIK_SEMIBOLD_FONT_PRELOAD,
];

const isPictureSource = (value: unknown): value is PictureSourceSpec[number] => {
  if (!value || typeof value !== 'object') return false;
  const source = value as Partial<PictureSourceSpec[number]>;
  return (
    typeof source.key === 'string' &&
    typeof source.srcSet === 'string' &&
    typeof source.media === 'string' &&
    (source.fallbackSrc === undefined || typeof source.fallbackSrc === 'string')
  );
};

export const getContentPageImagePreloads = (idToLoaderData: IIdToLoaderData): PictureSourceSpec =>
  Object.values(idToLoaderData)
    .flatMap((loaderData) =>
      Array.isArray(loaderData?.preloadImages) ? loaderData.preloadImages : [],
    )
    .filter(isPictureSource);
